import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { pl } from 'date-fns/locale';
import { format, parseISO } from 'date-fns';
import { getAuthHeaders } from '../../utils/auth';

// Same palette the event manager offers, so the site keeps a consistent look.
const BANNER_COLORS = [
  { value: '#5c4033', label: 'Brązowy (motyw strony)' },
  { value: '#2c5f2d', label: 'Zielony' },
  { value: '#2196f3', label: 'Niebieski' },
  { value: '#ff9800', label: 'Pomarańczowy' },
  { value: '#9c27b0', label: 'Fioletowy' },
  { value: '#f44336', label: 'Czerwony' },
  { value: '#607d8b', label: 'Szary' },
];

const emptyForm = {
  title: '',
  content: '',
  link_url: '',
  link_label: '',
  color: '#5c4033',
  active: true,
  active_from: null,
  active_until: null,
  dismissible: true,
  display_order: 0,
};

/**
 * Admin CRUD for the announcement bars shown on the public site.
 *
 * A banner becomes visible once it is active and "now" falls inside its
 * optional from/until window, which lets a promotion be prepared up front and
 * expire on its own without anyone going back into the panel.
 */
const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletingBanner, setDeletingBanner] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/admin/banners`, { headers });
      if (response.ok) {
        setBanners(await response.json());
        setError('');
      } else {
        setError(
          response.status === 403 || response.status === 401
            ? 'Brak uprawnień do pobrania banerów'
            : 'Nie udało się pobrać banerów'
        );
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = () => {
    setFormData(emptyForm);
    setEditingBanner(null);
    setDialogOpen(true);
  };

  const handleEdit = (banner) => {
    setFormData({
      title: banner.title || '',
      content: banner.content || '',
      link_url: banner.link_url || '',
      link_label: banner.link_label || '',
      color: banner.color || '#5c4033',
      active: banner.active ?? true,
      active_from: banner.active_from ? parseISO(banner.active_from) : null,
      active_until: banner.active_until ? parseISO(banner.active_until) : null,
      dismissible: banner.dismissible ?? true,
      display_order: banner.display_order ?? 0,
    });
    setEditingBanner(banner);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.content.trim()) {
      setError('Treść banera jest wymagana');
      return;
    }
    if (
      formData.active_from &&
      formData.active_until &&
      formData.active_until < formData.active_from
    ) {
      setError('Data zakończenia nie może być wcześniejsza niż data rozpoczęcia');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const payload = {
        title: formData.title.trim() || null,
        content: formData.content,
        link_url: formData.link_url.trim() || null,
        link_label: formData.link_label.trim() || null,
        color: formData.color,
        active: formData.active,
        active_from: formData.active_from ? formData.active_from.toISOString() : null,
        active_until: formData.active_until ? formData.active_until.toISOString() : null,
        dismissible: formData.dismissible,
        display_order: Number(formData.display_order) || 0,
      };

      const isEditing = editingBanner !== null;
      const url = isEditing
        ? `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/banners/${editingBanner.id}`
        : `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/banners`;

      const headers = await getAuthHeaders();
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccess(isEditing ? 'Baner zaktualizowany' : 'Baner utworzony');
        setDialogOpen(false);
        await fetchBanners();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(
          typeof errorData.detail === 'string'
            ? errorData.detail
            : 'Nie udało się zapisać banera'
        );
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingBanner) return;
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/admin/banners/${deletingBanner.id}`,
        { method: 'DELETE', headers }
      );
      if (response.ok) {
        setSuccess('Baner usunięty');
        await fetchBanners();
      } else {
        setError('Nie udało się usunąć banera');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
      setDeletingBanner(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try {
      return format(parseISO(value), 'dd.MM.yyyy HH:mm', { locale: pl });
    } catch {
      return value;
    }
  };

  // A banner is only on the site when it is active AND inside its window.
  const isLive = (banner) => {
    if (!banner.active) return false;
    const now = new Date();
    if (banner.active_from && parseISO(banner.active_from) > now) return false;
    if (banner.active_until && parseISO(banner.active_until) < now) return false;
    return true;
  };

  const columns = [
    {
      field: 'content',
      headerName: 'Treść',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.row.title && <strong>{params.row.title} — </strong>}
          {params.value}
        </Box>
      ),
    },
    {
      field: 'color',
      headerName: 'Kolor',
      width: 70,
      renderCell: (params) => (
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: params.value || '#5c4033',
            border: '2px solid rgba(0,0,0,0.1)',
          }}
          title={params.value}
        />
      ),
    },
    {
      field: 'active_from',
      headerName: 'Od',
      width: 150,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'active_until',
      headerName: 'Do',
      width: 150,
      renderCell: (params) => formatDate(params.value),
    },
    { field: 'display_order', headerName: 'Kolejność', width: 100 },
    {
      field: 'active',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        if (!params.row.active) {
          return <Chip label="Wyłączony" size="small" color="default" />;
        }
        return isLive(params.row) ? (
          <Chip label="Na stronie" size="small" color="success" />
        ) : (
          <Chip label="Zaplanowany" size="small" color="warning" />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Akcje',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleEdit(params.row)} size="small">
            <EditIcon />
          </IconButton>
          <IconButton onClick={() => setDeletingBanner(params.row)} size="small" color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              <CampaignIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Banery na stronie
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ogłoszenia wyświetlane na górze strony głównej. Baner pojawia się, gdy jest aktywny
              i mieści się w zadanym zakresie dat.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ textTransform: 'none' }}
          >
            Nowy baner
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={banners}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            loading={loading}
            disableSelectionOnClick
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingBanner ? 'Edytuj baner' : 'Nowy baner'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Tytuł (opcjonalnie)"
                value={formData.title}
                onChange={(e) => handleFormChange('title', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Treść"
                value={formData.content}
                onChange={(e) => handleFormChange('content', e.target.value)}
                margin="normal"
                multiline
                rows={3}
                required
              />

              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    label="Link (opcjonalnie)"
                    placeholder="https://... lub #pricing"
                    value={formData.link_url}
                    onChange={(e) => handleFormChange('link_url', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="Tekst przycisku"
                    placeholder="Zobacz"
                    value={formData.link_label}
                    onChange={(e) => handleFormChange('link_label', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Widoczny od"
                    value={formData.active_from}
                    onChange={(v) => handleFormChange('active_from', v)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Widoczny do"
                    value={formData.active_until}
                    onChange={(v) => handleFormChange('active_until', v)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Kolejność"
                    helperText="Mniejsza liczba = wyżej"
                    value={formData.display_order}
                    onChange={(e) => handleFormChange('display_order', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Kolor tła banera
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => handleFormChange('color', e.target.value)}
                    style={{
                      width: 50,
                      height: 40,
                      padding: 0,
                      border: '2px solid #ccc',
                      borderRadius: 8,
                      cursor: 'pointer',
                    }}
                    title="Wybierz dowolny kolor"
                  />
                  <TextField
                    size="small"
                    value={formData.color}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
                        handleFormChange('color', value || '#5c4033');
                      }
                    }}
                    sx={{ width: 110 }}
                    inputProps={{ maxLength: 7 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {BANNER_COLORS.map((option) => (
                    <Box
                      key={option.value}
                      onClick={() => handleFormChange('color', option.value)}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        backgroundColor: option.value,
                        cursor: 'pointer',
                        border: formData.color === option.value ? '3px solid #333' : '2px solid transparent',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                      title={option.label}
                    />
                  ))}
                </Box>
              </Box>

              {/* Live preview so the admin sees the colour/text combination */}
              <Box
                sx={{
                  mt: 3,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: formData.color,
                  color: '#fff',
                }}
              >
                {formData.title && <strong>{formData.title} — </strong>}
                {formData.content || 'Podgląd banera'}
              </Box>

              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.active}
                      onChange={(e) => handleFormChange('active', e.target.checked)}
                    />
                  }
                  label="Aktywny"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.dismissible}
                      onChange={(e) => handleFormChange('dismissible', e.target.checked)}
                    />
                  }
                  label="Można zamknąć"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={loading}>
              Anuluj
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Zapisywanie...' : editingBanner ? 'Aktualizuj' : 'Utwórz'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={Boolean(deletingBanner)} onClose={() => setDeletingBanner(null)}>
          <DialogTitle>Potwierdź usunięcie</DialogTitle>
          <DialogContent>
            <Typography>Czy na pewno chcesz usunąć ten baner?</Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ta operacja jest nieodwracalna.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeletingBanner(null)} disabled={loading}>
              Anuluj
            </Button>
            <Button color="error" onClick={handleConfirmDelete} disabled={loading}>
              {loading ? 'Usuwanie...' : 'Usuń'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default BannerManagement;
